import React, { useState, useRef } from 'react';
import { useStudioTheme, motion, type StudioTokens } from './studioTheme';
import type { HealthStatus, KnowledgeStatus } from '../../../data/agentCommandCenter';

// ── Status helpers ────────────────────────────────────────────────────────────

export function healthColor(h: HealthStatus, ST: StudioTokens): string {
  return h === 'healthy' ? ST.status.healthy
       : h === 'degraded' ? ST.status.degraded
       : ST.status.down;
}
export function healthLabel(h: HealthStatus): string {
  return h === 'healthy' ? 'Healthy' : h === 'degraded' ? 'Degraded' : 'Down';
}
export function knowledgeColor(k: KnowledgeStatus, ST: StudioTokens): string {
  return k === 'in_sync' ? ST.status.inSync
       : k === 'needs_sync' ? ST.status.needsSync
       : ST.status.outdated;
}
export function knowledgeLabel(k: KnowledgeStatus): string {
  return k === 'in_sync' ? 'In Sync' : k === 'needs_sync' ? 'Needs Sync' : 'Outdated';
}
export function freshness(dateStr: string): string {
  const now  = new Date();
  const then = new Date(dateStr + 'T12:00:00');
  const diff = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (diff <= 0) return 'Updated today';
  if (diff === 1) return 'Updated yesterday';
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `Updated ${m[then.getMonth()]} ${then.getDate()}`;
}

// ── Animation helpers ─────────────────────────────────────────────────────────

export type AnimationMode = 'breathing' | 'wave' | 'warning' | 'spin' | 'shimmer' | 'none';

/** Maps workspace health to the appropriate StatusDot animation mode. */
export function healthToAnimation(h: HealthStatus): AnimationMode {
  if (h === 'healthy')  return 'breathing';
  if (h === 'degraded') return 'wave';
  return 'warning';
}

// ── Cursor tilt hook ──────────────────────────────────────────────────────────

export function useCursorTilt(maxDeg: number = 5) {
  const ref = useRef<HTMLDivElement>(null);
  const [t, setT] = useState({ rx: 0, ry: 0, gx: 50, gy: 50, on: false });

  const onMouseMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top)  / r.height;
    setT({ rx: (y - 0.5) * -maxDeg, ry: (x - 0.5) * maxDeg, gx: x * 100, gy: y * 100, on: true });
  };

  const onMouseLeave = () => setT({ rx: 0, ry: 0, gx: 50, gy: 50, on: false });

  return { ref, tilt: t, onMouseMove, onMouseLeave };
}

// ── AnimatedBorderCard ────────────────────────────────────────────────────────

export function AnimatedBorderCard({
  children,
  style = {},
  innerStyle = {},
}: {
  children:   React.ReactNode;
  style?:     React.CSSProperties;
  innerStyle?: React.CSSProperties;
}) {
  const { ST } = useStudioTheme();
  return (
    <div
      className="studio-border-live"
      style={{ padding: 1, borderRadius: ST.radius.xl + 1, ...style }}
    >
      <div style={{
        backgroundColor: ST.bg.surface,
        borderRadius:    ST.radius.xl,
        overflow:        'hidden',
        ...innerStyle,
      }}>
        {children}
      </div>
    </div>
  );
}

// ── StatusDot ─────────────────────────────────────────────────────────────────

export function StatusDot({
  color,
  size = 6,
  animate = 'none',
}: {
  color:   string;
  size?:   number;
  animate?: AnimationMode;
}) {
  const dotStyle: React.CSSProperties = {
    display:         'inline-block',
    width:           size,
    height:          size,
    borderRadius:    '50%',
    backgroundColor: color,
    flexShrink:      0,
    boxShadow:       `0 0 8px ${color}cc, 0 0 16px ${color}40`,
  };

  // Wave needs a separate expanding ring rendered behind the dot
  if (animate === 'wave') {
    return (
      <span style={{
        position:       'relative',
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          size,
        height:         size,
        flexShrink:     0,
      }}>
        {/* Ring expands outward and fades — color set via inline style, scale handled by CSS */}
        <span
          className="studio-anim-wave"
          style={{
            position:        'absolute',
            width:           size,
            height:          size,
            borderRadius:    '50%',
            backgroundColor: color,
          }}
        />
        {/* Solid dot sits on top of the ring */}
        <span style={{ ...dotStyle, position: 'relative', zIndex: 1 }} />
      </span>
    );
  }

  const animClass =
    animate === 'breathing' ? 'studio-anim-breathe' :
    animate === 'warning'   ? 'studio-anim-warn'    :
    animate === 'spin'      ? 'studio-anim-spin'    :
    animate === 'shimmer'   ? 'studio-anim-shimmer' : '';

  return (
    <span
      className={animClass || undefined}
      style={dotStyle}
    />
  );
}

// ── GlassCard ─────────────────────────────────────────────────────────────────

export function GlassCard({
  children,
  onClick,
  isSelected = false,
  style = {},
  accentColor,
}: {
  children:     React.ReactNode;
  onClick?:     () => void;
  isSelected?:  boolean;
  style?:       React.CSSProperties;
  accentColor?: string;
}) {
  const { ST } = useStudioTheme();
  const [hovered,  setHovered]  = useState(false);
  const [pressed,  setPressed]  = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => onClick && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        position:          'relative',
        backgroundColor:   hovered || isSelected ? ST.glass.surfaceHover : ST.glass.surface,
        backdropFilter:    `blur(${ST.glass.blur})`,
        WebkitBackdropFilter: `blur(${ST.glass.blur})`,
        border:            `1px solid ${isSelected ? (accentColor ?? ST.border.accent) : hovered ? ST.glass.borderHover : ST.glass.border}`,
        borderRadius:      ST.radius.xl,
        boxShadow:         isSelected
          ? `${ST.shadow.cardActive}, 0 0 32px ${accentColor ? accentColor + '30' : ST.accent.purpleGlow}, ${ST.shadow.inset}`
          : hovered
          ? `${ST.shadow.cardHover}, ${ST.shadow.inset}`
          : `${ST.shadow.card}, ${ST.shadow.inset}`,
        transform:         pressed
          ? 'scale(0.97) translateY(1px)'
          : hovered && !isSelected ? 'translateY(-3px)' : 'translateY(0)',
        transition:        motion(`transform ${ST.motion.spring}, box-shadow 0.20s ease, border-color 0.20s ease, background 0.20s ease`),
        cursor:            onClick ? 'pointer' : 'default',
        overflow:          'hidden',
        ...style,
      }}
    >
      {/* Inner glass top-edge reflection */}
      <div style={{
        position:      'absolute',
        top:           0,
        left:          16,
        right:         16,
        height:        1,
        background:    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.11) 28%, rgba(255,255,255,0.11) 72%, transparent 100%)',
        pointerEvents: 'none',
        zIndex:        0,
      }} />
      {/* Accent stripe on left edge when selected */}
      {isSelected && (
        <div style={{
          position:        'absolute',
          left:            0,
          top:             0,
          bottom:          0,
          width:           3,
          borderRadius:    `${ST.radius.xl}px 0 0 ${ST.radius.xl}px`,
          backgroundColor: accentColor ?? ST.accent.purple,
        }} />
      )}
      {children}
    </div>
  );
}

// ── StudioBadge ───────────────────────────────────────────────────────────────

export type BadgeVariant =
  | 'healthy' | 'degraded' | 'down'
  | 'active' | 'paused' | 'blocked' | 'inprogress' | 'default';

export function StudioBadge({ label, variant = 'default' }: { label: string; variant?: BadgeVariant }) {
  const { ST } = useStudioTheme();

  const BADGE: Record<BadgeVariant, { color: string; bg: string; border: string }> = {
    healthy:    { color: ST.status.healthy,          bg: ST.status.healthyBg,   border: ST.status.healthyBorder   },
    degraded:   { color: ST.status.degraded,         bg: ST.status.degradedBg,  border: ST.status.degradedBorder  },
    down:       { color: ST.status.down,             bg: ST.status.downBg,      border: ST.status.downBorder      },
    active:     { color: '#a78bfa',                  bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.28)' },
    paused:     { color: ST.text.disabled,           bg: ST.bg.overlay,         border: ST.border.subtle          },
    blocked:    { color: ST.status.degraded,         bg: ST.status.degradedBg,  border: ST.status.degradedBorder  },
    inprogress: { color: '#f97316',                  bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.28)' },
    default:    { color: ST.text.disabled,           bg: ST.bg.overlay,         border: ST.border.subtle          },
  };

  const s = BADGE[variant];
  return (
    <span style={{
      display:         'inline-flex',
      alignItems:      'center',
      fontSize:        10,
      fontWeight:      600,
      fontFamily:      ST.font.sans,
      letterSpacing:   '0.07em',
      textTransform:   'uppercase',
      color:           s.color,
      backgroundColor: s.bg,
      border:          `1px solid ${s.border}`,
      borderRadius:    ST.radius.sm,
      padding:         '3px 8px',
      flexShrink:      0,
    }}>
      {label}
    </span>
  );
}

// ── StudioCard ────────────────────────────────────────────────────────────────

export function StudioCard({
  children,
  onClick,
  isSelected = false,
  style = {},
}: {
  children:    React.ReactNode;
  onClick?:    () => void;
  isSelected?: boolean;
  style?:      React.CSSProperties;
}) {
  const { ST } = useStudioTheme();
  const [hovered, setHovered] = useState(false);

  const bgColor = isSelected ? ST.bg.surfaceActive
                : hovered    ? ST.bg.surfaceHover
                :              ST.bg.surface;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: bgColor,
        backgroundImage: ST.gradient.card,
        border: `1px solid ${
          isSelected ? ST.border.accent
          : hovered   ? ST.border.default
          :             ST.border.subtle
        }`,
        borderRadius: ST.radius.lg,
        boxShadow: isSelected
          ? ST.shadow.cardActive
          : hovered
          ? ST.shadow.cardHover
          : ST.shadow.card,
        transform:  hovered && !isSelected ? 'translateY(-2px)' : 'translateY(0)',
        transition: motion(ST.motion.lift),
        cursor:     onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── StudioActionButton ────────────────────────────────────────────────────────

export function StudioActionButton({
  icon,
  label,
  description,
  onClick,
  accent = false,
  disabled = false,
}: {
  icon:         React.ReactNode;
  label:        string;
  description?: string;
  onClick?:     () => void;
  accent?:      boolean;
  disabled?:    boolean;
}) {
  const { ST } = useStudioTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:         'flex',
        alignItems:      'center',
        gap:             ST.space[3],
        padding:         `${ST.space[2] + 2}px ${ST.space[3]}px`,
        borderRadius:    ST.radius.md,
        backgroundColor: hovered && !disabled ? ST.bg.surfaceHover : 'transparent',
        border:          `1px solid ${hovered && !disabled ? ST.border.subtle : 'transparent'}`,
        cursor:          disabled ? 'not-allowed' : 'pointer',
        opacity:         disabled ? 0.45 : 1,
        transition:      motion(ST.motion.default),
      }}
    >
      <div style={{
        width:           32,
        height:          32,
        borderRadius:    ST.radius.sm,
        backgroundColor: accent ? ST.accent.purpleSubtle : ST.bg.overlay,
        border:          `1px solid ${accent ? ST.border.accent : ST.border.subtle}`,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        flexShrink:      0,
        fontSize:        15,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize:      12,
          fontWeight:    500,
          color:         accent ? ST.text.accent : ST.text.primary,
          fontFamily:    ST.font.sans,
          lineHeight:    1.3,
          whiteSpace:    'nowrap',
          overflow:      'hidden',
          textOverflow:  'ellipsis',
        }}>
          {label}
        </div>
        {description && (
          <div style={{
            fontSize:     11,
            color:        ST.text.muted,
            fontFamily:   ST.font.sans,
            lineHeight:   1.3,
            marginTop:    1,
            whiteSpace:   'nowrap',
            overflow:     'hidden',
            textOverflow: 'ellipsis',
          }}>
            {description}
          </div>
        )}
      </div>
      <span style={{
        fontSize:   12,
        color:      ST.text.disabled,
        transform:  hovered && !disabled ? 'translateX(2px)' : 'translateX(0)',
        transition: motion(ST.motion.default),
        flexShrink: 0,
      }}>
        →
      </span>
    </div>
  );
}

// ── CopyButton ────────────────────────────────────────────────────────────────

export function CopyButton({
  text,
  label = 'Copy',
  primary = false,
}: {
  text:     string;
  label?:   string;
  primary?: boolean;
}) {
  const { ST } = useStudioTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (primary) {
    return (
      <button
        onClick={handleCopy}
        style={{
          width:           '100%',
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'space-between',
          fontSize:        13,
          fontWeight:      500,
          color:           copied ? ST.status.healthy : ST.text.gold,
          backgroundColor: copied ? ST.status.healthyBg : ST.accent.goldSubtle,
          border:          `1px solid ${copied ? ST.status.healthyBorder : ST.border.gold}`,
          borderRadius:    ST.radius.md,
          padding:         '12px 16px',
          cursor:          'pointer',
          fontFamily:      ST.font.sans,
          textAlign:       'left',
          transition:      motion(ST.motion.default),
        }}
      >
        <span>{copied ? '✓ Copied' : label}</span>
        {!copied && <span style={{ opacity: 0.5 }}>→</span>}
      </button>
    );
  }

  return (
    <button
      onClick={handleCopy}
      style={{
        width:           '100%',
        fontSize:        11,
        color:           copied ? ST.status.healthy : ST.text.muted,
        backgroundColor: ST.bg.overlay,
        border:          `1px solid ${ST.border.subtle}`,
        borderRadius:    ST.radius.md,
        padding:         '8px 14px',
        cursor:          'pointer',
        fontFamily:      ST.font.sans,
        textAlign:       'left',
        transition:      motion(ST.motion.default),
      }}
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}

// ── Divider ───────────────────────────────────────────────────────────────────

export function Divider({ style = {} }: { style?: React.CSSProperties }) {
  const { ST } = useStudioTheme();
  return (
    <div style={{
      height:          1,
      backgroundColor: ST.border.subtle,
      ...style,
    }} />
  );
}

// ── PanelLabel ────────────────────────────────────────────────────────────────

export function PanelLabel({ children }: { children: React.ReactNode }) {
  const { ST } = useStudioTheme();
  return (
    <div style={{
      fontSize:      10,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color:         ST.text.disabled,
      fontFamily:    ST.font.sans,
      fontWeight:    600,
    }}>
      {children}
    </div>
  );
}
